import apiClient from './api'

export interface ForecastRequest {
  metric: 'deposits' | 'withdrawals' | 'net' | 'volume'
  days: 7 | 30 | 90
  startDate?: string
  endDate?: string
}

export interface ForecastDataPoint {
  date: string
  label: string
  actual?: number
  forecast: number
  upperBound?: number
  lowerBound?: number
}

export interface ForecastResponse {
  data: ForecastDataPoint[]
  accuracy?: number
  model: string
  confidence: number
}

// Simple linear regression forecast (can be replaced with more sophisticated models)
const simpleLinearForecast = (historical: number[], periods: number): number[] => {
  if (historical.length < 2) {
    // If not enough data, use average
    const avg = historical.reduce((a, b) => a + b, 0) / historical.length
    return Array(periods).fill(avg)
  }

  // Calculate trend
  const n = historical.length
  const sumX = (n * (n - 1)) / 2
  const sumY = historical.reduce((a, b) => a + b, 0)
  const sumXY = historical.reduce((sum, y, x) => sum + x * y, 0)
  const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  // Generate forecast
  const forecast: number[] = []
  for (let i = 0; i < periods; i++) {
    const x = n + i
    const y = slope * x + intercept
    forecast.push(Math.max(0, y)) // Ensure non-negative
  }

  return forecast
}

export const predictiveService = {
  generateForecast: async (request: ForecastRequest): Promise<ForecastResponse> => {
    try {
      // For now, use volume report to get historical data
      const endDate = request.endDate || new Date().toISOString().split('T')[0]
      const startDate = request.startDate || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      const response = await apiClient.get('/reports/volumes', {
        params: {
          startDate,
          endDate,
          groupBy: 'day',
        },
      })

      const historical = response.data.breakdown || []
      const historicalValues = historical.map((point: any) => {
        switch (request.metric) {
          case 'deposits':
            return point.deposits || 0
          case 'withdrawals':
            return point.withdrawals || 0
          case 'net':
            return (point.deposits || 0) - (point.withdrawals || 0)
          case 'volume':
            return (point.deposits || 0) + (point.withdrawals || 0)
          default:
            return 0
        }
      })

      // Generate forecast
      const forecastValues = simpleLinearForecast(historicalValues, request.days)

      // Create data points
      const data: ForecastDataPoint[] = []
      const today = new Date()

      // Historical data
      historical.forEach((point: any, index: number) => {
        const date = new Date(point.label)
        data.push({
          date: date.toISOString(),
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          actual: historicalValues[index],
          forecast: historicalValues[index], // For historical, forecast = actual
        })
      })

      // Forecast data
      for (let i = 0; i < request.days; i++) {
        const date = new Date(today)
        date.setDate(date.getDate() + i + 1)
        const forecastValue = forecastValues[i]
        const stdDev = Math.sqrt(
          historicalValues.reduce((sum: number, val: number) => {
            const mean = historicalValues.reduce((a: number, b: number) => a + b, 0) / historicalValues.length
            return sum + Math.pow(val - mean, 2)
          }, 0) / historicalValues.length
        )

        data.push({
          date: date.toISOString(),
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          forecast: forecastValue,
          upperBound: forecastValue + 1.96 * stdDev, // 95% confidence
          lowerBound: Math.max(0, forecastValue - 1.96 * stdDev),
        })
      }

      // Calculate accuracy based on historical data variance
      // In production, this would be calculated from past forecast accuracy
      const variance = historicalValues.length > 0
        ? historicalValues.reduce((sum: number, val: number) => {
            const mean = historicalValues.reduce((a: number, b: number) => a + b, 0) / historicalValues.length
            return sum + Math.pow(val - mean, 2)
          }, 0) / historicalValues.length
        : 0
      const accuracy = historicalValues.length > 0 ? Math.max(70, Math.min(95, 100 - (variance / 100))) : 0

      return {
        data,
        accuracy,
        model: 'linear-regression',
        confidence: 0.95,
      }
    } catch (error) {
      console.error('Failed to generate forecast', error)
      throw error
    }
  },
}

